import React, { useState, useEffect } from "react";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import { useQueryParameters } from "@/hooks/useQueryParameters";
import {
  useQueryGetExamReports,
  type IExamReportsParams,
} from "@/apis/exam_reports/getExamReports";
import { getPaginationInfo } from "@/types/pagination";
import ExamResultsTable from "./ExamResultsTable";

type SortField =
  | "user_name"
  | "exam_set_title"
  | "exam_type"
  | "total_score"
  | "created_at"
  | null;
type SortOrder = "asc" | "desc";

const ExamResultListPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");

  // Configure query parameters with localStorage persistence
  const { params, setParam, setParams } = useQueryParameters({
    page: {
      defaultValue: 1,
      removeWhenDefault: true,
    },
    search: {
      defaultValue: "",
      removeWhenDefault: true,
    },
    sort: {
      defaultValue: null as SortField,
      removeWhenDefault: true,
      persist: true,
    },
    order: {
      defaultValue: "desc" as SortOrder,
      removeWhenDefault: true,
      persist: true,
    },
    limit: {
      defaultValue: 10,
      removeWhenDefault: true,
    },
  }, {
    storageKey: "examResultListSettings",
  });

  // Sync search input with URL param
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  // API 호출
  const { data, isLoading, error } = useQueryGetExamReports({
    page: params.page,
    limit: params.limit,
    search: params.search || undefined,
    sort: params.sort || undefined,
    sort_order: params.sort ? params.order : undefined,
  });

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({
      search: searchInput,
      page: 1,
    });
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (params.sort === field) {
      if (params.order === "asc") {
        // asc → desc
        setParam("order", "desc");
      } else {
        // desc → 정렬 해제
        setParams({
          sort: null,
          order: "desc",
        });
      }
    } else {
      // New field clicked, set to ascending
      setParams({
        sort: field,
        order: "asc",
        page: 1,
      });
    }
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setParam("page", page);
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Results</Heading>
        <Text className="text-zinc-500">
          View and manage completed exam results from students. Use search to
          find specific results.
        </Text>

        <div className="flex justify-end mt-4">
          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="student name, email, exam set"
                aria-label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Exam Results Table Component */}
        <ExamResultsTable
          examResults={data?.items || []}
          isLoading={isLoading}
          error={error as Error | null}
          sortField={params.sort}
          sortOrder={params.order}
          onSort={handleSort}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default ExamResultListPage;
